import React from 'react';
import { Edit2, Trash2, Check } from 'lucide-react';

/**
 * Reusable ThemeCard — used on the Theme Manager page and inline in the RFP intake form.
 *
 * Props:
 *   theme        : { id, name, primaryColor, secondaryColor, isCustom }
 *   isSelected   : bool — shows selected ring
 *   compact      : bool — smaller variant for inline selectors
 *   showActions  : bool — show edit/delete on hover (only for custom themes)
 *   onSelect     : () => void
 *   onEdit       : (theme) => void
 *   onDelete     : (theme) => void
 */
const ThemeCard = ({
    theme,
    isSelected = false,
    compact = false,
    showActions = false,
    onSelect,
    onEdit,
    onDelete,
}) => {
    const [hovered, setHovered] = React.useState(false);

    const w = compact ? '96px' : '130px';
    const h = compact ? '82px' : '110px';
    const fontSz = compact ? '24px' : '32px';
    const swatchSz = compact ? '10px' : '12px';
    const nameSz = compact ? '11px' : '12px';

    return (
        <div
            onClick={onSelect}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                width: w, height: h, flexShrink: 0,
                border: isSelected ? '2px solid #FF5526' : hovered ? '1.5px solid #FFB399' : '1px solid #CBD5E1',
                borderRadius: '10px', padding: compact ? '10px' : '12px',
                cursor: onSelect ? 'pointer' : 'default',
                display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                background: isSelected ? '#F0F7FF' : '#FFFFFF',
                position: 'relative', transition: 'all 0.2s',
                boxShadow: hovered ? '0 4px 12px rgba(0,0,0,0.08)' : isSelected ? '0 0 0 4px rgba(255, 85, 38,0.1)' : 'none',
            }}
        >
            {/* Selected tick */}
            {isSelected && (
                <div style={{
                    position: 'absolute', top: '6px', right: '6px',
                    background: '#FF5526', borderRadius: '50%',
                    width: '18px', height: '18px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <Check size={11} color="white" strokeWidth={3} />
                </div>
            )}

            {/* Preview typography */}
            <div style={{ fontSize: fontSz, lineHeight: '1', fontWeight: '500', color: theme.primaryColor, fontFamily: 'serif' }}>
                Aa
            </div>

            {/* Color swatches + name */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <div style={{ display: 'flex', gap: '4px' }}>
                    {[theme.primaryColor, theme.secondaryColor].map((c, i) => (
                        <div key={i} style={{ width: swatchSz, height: swatchSz, background: c, borderRadius: '2px' }} />
                    ))}
                </div>
                <div style={{
                    fontSize: nameSz, fontWeight: '500', color: '#475569',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    fontFamily: 'system-ui, sans-serif'
                }} title={theme.name}>
                    {theme.name}
                </div>
            </div>

            {/* Edit / Delete actions (hover, custom themes only) */}
            {showActions && hovered && theme.isCustom && (
                <div style={{
                    position: 'absolute', top: '6px', right: '6px',
                    display: 'flex', gap: '4px', background: 'rgba(255,255,255,0.95)',
                    padding: '2px', borderRadius: '4px', boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
                }}>
                    <button
                        onClick={(e) => { e.stopPropagation(); onEdit?.(theme); }}
                        style={{ background: 'none', border: 'none', padding: '4px', cursor: 'pointer', color: '#64748B', display: 'flex', alignItems: 'center' }}
                        title="Edit"
                    >
                        <Edit2 size={12} />
                    </button>
                    <button
                        onClick={(e) => { e.stopPropagation(); onDelete?.(theme); }}
                        style={{ background: 'none', border: 'none', padding: '4px', cursor: 'pointer', color: '#EF4444', display: 'flex', alignItems: 'center' }}
                        title="Delete"
                    >
                        <Trash2 size={12} />
                    </button>
                </div>
            )}
        </div>
    );
};

export default ThemeCard;
